import { useState } from 'react'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { selectAgentV2ConversationDetail, selectAgentV2ConversationFromId } from '@/agent-v2/conversations/store/selectors'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { deleteAgentV2Conversation } from '@/agent-v2/conversations/fetch/delete-agent-v2-conversation'
import { patchRenameAgentV2ConversationTitle } from '@/agent-v2/conversations/fetch/patch-rename-agent-v2-conversation-title'
import RenameChatModal from '@/chat-common/components/dialogs/RenameChatModal'
import DeleteChatModal from '@/chat-common/components/dialogs/DeleteChatModal'
import { useIsAgentV2ConversationCreator } from '@/agent-v2/conversations/hooks/useIsAgentV2ConversationCreator'
import type { ChatListSidebarItem } from '@/chat-common/conversation_list/schema'

type AgentV2ConversationDialogProps = {
  conversationId: string | null
  kind: 'delete' | 'rename'
  onClose: () => void
  open: boolean
  /* Prefills the rename field in place of the saved title */
  displayedTitle?: string
  /** Overrides the default navigation after deleting the active chat. */
  onDeleted?: () => void
}

type AgentV2ConversationDialogItem = ChatListSidebarItem & {
  matter_id: string
}

export default function AgentV2ConversationDialog({ conversationId, kind, onClose, open, displayedTitle, onDeleted }: AgentV2ConversationDialogProps) {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const conversation = useAppSelector((state: RootState) => selectAgentV2ConversationFromId(state, { conversationId: conversationId ?? undefined }))
  const conversationDetail = useAppSelector((state: RootState) => selectAgentV2ConversationDetail(state, { conversationId: conversationId ?? undefined }))
  const isConversationCreator = useIsAgentV2ConversationCreator(conversationId ?? undefined)
  const [deletedConversationItem, setDeletedConversationItem] = useState<AgentV2ConversationDialogItem | null>(null)

  const conversationItem = conversation
    ? {
        id: conversation.id,
        title: displayedTitle ?? conversation.title,
        matter_id: conversation.matter_id,
        modified_on: Date.parse(conversation.updated_at),
      }
    : null

  if (kind === 'rename') {
    if (!conversation || !conversationItem || !isConversationCreator) return null

    return (
      <RenameChatModal
        chatItem={conversationItem}
        dialogVisible={open}
        onDialogClose={onClose}
        renameConversation={async (input) => {
          const lifecycleGeneration = store.getState().agentV2ConversationsState.lifecycleGeneration
          const updatedConversation = await patchRenameAgentV2ConversationTitle(input)
          if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return

          dispatch(AgentV2ConversationsActions.upsertConversationFromSummary(updatedConversation))
          dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId: conversation.matter_id, forceRefresh: true }))
          if (conversationDetail) {
            dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId: input.conversationId, forceRefresh: true }))
          }
        }}
      />
    )
  }

  const retainedDeletedItem = deletedConversationItem && (deletedConversationItem.id === conversationId || !open) ? deletedConversationItem : null
  const deleteDialogItem = conversationItem ?? retainedDeletedItem
  if (!deleteDialogItem || (!isConversationCreator && !retainedDeletedItem)) return null

  return (
    <DeleteChatModal
      onDeleted={onDeleted}
      chatItem={deleteDialogItem}
      deleteConversation={async (conversationIdToDelete) => {
        const lifecycleGeneration = store.getState().agentV2ConversationsState.lifecycleGeneration
        dispatch(AgentV2ConversationsActions.conversationDeletionStarted({ conversationId: conversationIdToDelete }))

        try {
          await deleteAgentV2Conversation(conversationIdToDelete)
          if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return

          setDeletedConversationItem(deleteDialogItem)
          dispatch(AgentV2ConversationsActions.deleteConversation({ conversationId: conversationIdToDelete }))
          dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId: deleteDialogItem.matter_id, forceRefresh: true }))
        } catch (error) {
          if (store.getState().agentV2ConversationsState.lifecycleGeneration === lifecycleGeneration) {
            dispatch(AgentV2ConversationsActions.conversationDeletionFailed({ conversationId: conversationIdToDelete }))
            dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId: deleteDialogItem.matter_id, forceRefresh: true }))
            dispatch(
              AgentV2ConversationsActions.fetchConversationDetail({
                conversationId: conversationIdToDelete,
                forceRefresh: true,
                treatNotFoundAsDeleted: true,
              })
            )
          }
          throw error
        }
      }}
      dialogVisible={open}
      onDialogClose={onClose}
    />
  )
}
